import { apiClient, endpoints } from "@/api";

import type {
	InviteUserPayload,
	RemoveUserResponse,
	UpdateUserPayload,
	UpdateUserPermissionsPayload,
	UpdateUserStatusPayload,
	UserResponse,
} from "./types";

/**
 * Users API Service
 *
 * Pure functions for user-related API calls.
 * No React dependencies - can be used anywhere.
 *
 * This is the API layer - it only handles HTTP communication.
 * Business logic and state management happen in React Query hooks.
 */

export const usersApi = {
	/**
	 * Cancel a pending invite for a user
	 */
	cancelInvite: async (userId: string): Promise<void> => {
		await apiClient.delete(endpoints.users.cancelInvite(userId));
	},

	/**
	 * Get a single user by ID
	 */
	getUser: async (userId: string): Promise<UserResponse> => {
		const response = await apiClient.get<UserResponse>(
			endpoints.users.getById(userId),
		);
		return response.data;
	},

	/**
	 * Get all users for the current organization
	 */
	getUsers: async (): Promise<UserResponse[]> => {
		const response = await apiClient.get<UserResponse[]>(endpoints.users.get);
		return response.data;
	},

	/**
	 * Invite a new user to the organization
	 */
	inviteUser: async (payload: InviteUserPayload): Promise<UserResponse> => {
		const response = await apiClient.post<UserResponse>(
			endpoints.users.invite,
			payload,
		);
		return response.data;
	},

	/**
	 * Remove a user from the organization
	 */
	removeUser: async (userId: string): Promise<RemoveUserResponse> => {
		const response = await apiClient.delete<RemoveUserResponse>(
			endpoints.users.delete(userId),
		);
		return response.data;
	},

	/**
	 * Update a user's profile (phone and/or notification types)
	 */
	updateUser: async (
		userId: string,
		payload: UpdateUserPayload,
	): Promise<UserResponse> => {
		const response = await apiClient.put<UserResponse>(
			endpoints.users.update(userId),
			payload,
		);
		return response.data;
	},

	/**
	 * Replace a user's granular permissions and location scope.
	 */
	updateUserPermissions: async (
		userId: string,
		payload: UpdateUserPermissionsPayload,
	): Promise<UserResponse> => {
		const response = await apiClient.put<UserResponse>(
			endpoints.users.updatePermissions(userId),
			payload,
		);
		return response.data;
	},

	/**
	 * Update a user's status (activate/deactivate)
	 */
	updateUserStatus: async (
		userId: string,
		payload: UpdateUserStatusPayload,
	): Promise<UserResponse> => {
		const response = await apiClient.put<UserResponse>(
			endpoints.users.updateStatus(userId),
			payload,
		);
		return response.data;
	},
};
